import { useState, useCallback } from 'react'
import { useGroupClientState } from './'
import { useQueryClient, useQuery, useMutation, useMutationState } from '@tanstack/react-query'
import { get, post, patch, remove } from 'api'
import { getGroupsInfiniteQuery } from 'state/groups'
import { useUpdateUsersByGroupCache } from 'state/users'

const namespace = 'group'

export const getGroupQuery = (type, id, options = {}) => ({
    ...options,
    queryKey: [namespace, type, id],
    queryFn: () => get({ path: `/groups/${id}` })
})

export const useGroup = (options = {}) => {
    const clientState = useGroupClientState(state => ({
        queryOptions: state.queryOptions,
        type: state.type,
        id: state.id
    }), true)

    const queryOptions = {
        ...(clientState?.queryOptions ?? null),
        ...(options?.queryOptions ?? null)
    }

    const [enabled, setEnabled] = useState(queryOptions?.enabled ?? true)
    const enable = useCallback(() => setEnabled(true), [])

    const type = options?.type ?? clientState?.type
    const id = options?.id ?? clientState?.id

    const deleted = useGroupDeleted()

    const queryEnabled = enabled && !!type && !!id && !deleted

    const {
        data,
        ...rest
    } = useQuery({
        ...getGroupQuery(type, id, queryOptions),
        enabled: queryEnabled
    })

    return {
        ...rest,
        data,
        item: data?.response ?? null,
        enabled: queryEnabled,
        enable
    }
}

export const useFetchGroup = () => {
    const queryClient = useQueryClient()

    return (type, id, queryOptions) => {
        if(!type || !id) {
            return null
        }

        return queryClient.fetchQuery(getGroupQuery(type, id, {
            ...(queryOptions ?? null),
            staleTime: 0 // As it is imperative, there will be no live subscription to new data
        }))
    }
}

export const useCreateGroup = (options = {}) => {
    const queryClient = useQueryClient()
    const clientState = useGroupClientState(state => ({ type: state.type }), true)

    const updateGroupsByTypeCache = useUpdateGroupsByTypeCache()

    return useMutation({
        gcTime: 15 * 1000,
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'create'],
        mutationFn: ({ type, body }) => post({
            path: '/groups',
            body: {
                ...body,
                type: body?.type ?? type ?? clientState?.type
            }
        }),
        onSuccess: (result, variables) => {
            if(!result.ok) {
                return
            }

            queryClient.setQueryData(
                getGroupQuery(result.response.type, result.response.id).queryKey,
                result
            )

            // Update the list of groups-of-type to reflect the created group
            updateGroupsByTypeCache(variables, {
                method: 'invalidateQueries',
                recount: true
            })
        }
    })
}

export const useUpdateGroup = (options = {}) => {
    const queryClient = useQueryClient()

    const clientState = useGroupClientState(state => ({
        type: state.type,
        id: state.id
    }), true)

    const updateGroupsByTypeCache = useUpdateGroupsByTypeCache()

    return useMutation({
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'update'],
        mutationFn: ({ type, id, body }) => patch({
            path: `/groups/${body?.id ?? id ?? clientState?.id}`,
            body: {
                ...body,
                type: body?.type ?? type ?? clientState?.type
            }
        }),
        onSuccess: result => {
            if(!result.ok) {
                return
            }

            queryClient.setQueryData(
                getGroupQuery(result.response.type, result.response.id).queryKey,
                result
            )

            // Update the list of groups-of-type to reflect the updated group
            // Use the response data here, in case the group was converted (type changed)
            updateGroupsByTypeCache(result.response, { method: 'invalidateQueries' })
        }
    })
}

export const useConvertGroup = (options = {}) => {
    const queryClient = useQueryClient()

    const clientState = useGroupClientState(state => ({
        type: state.type,
        id: state.id
    }), true)

    const updateGroupsByTypeCache = useUpdateGroupsByTypeCache()

    return useMutation({
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'convert'],
        mutationFn: ({ id, body }) => patch({
            path: `/groups/${body?.id ?? id ?? clientState?.id}/convert`,
            body
        }),
        onSuccess: (result, variables) => {
            if(!result.ok) {
                return
            }

            // Move the cache entry to the new group type
            queryClient.removeQueries({
                queryKey: getGroupQuery(variables.type, result.response.id).queryKey,
                exact: true
            })

            queryClient.setQueryData(
                getGroupQuery(result.response.type, result.response.id).queryKey,
                result
            )

            // Update the list of both groups-of-type to reflect the converted group
            // Use the response data here, in case the group was converted (type changed)
            updateGroupsByTypeCache(result.response, { method: 'invalidateQueries' })

            // Use the variables here, to get a hold of the previous group type
            updateGroupsByTypeCache(variables, { method: 'invalidateQueries' })
        }
    })
}

export const useGroupConverted = () => {
    const [converted = false] = useMutationState({
        filters: {
            mutationKey: [namespace, 'convert'],
            exact: true,
            status: 'success'
        },
        select: ({ state }) => !!state?.data?.ok
    })

    return converted
}

export const useDeleteGroup = (options = {}) => {
    const queryClient = useQueryClient()

    const clientState = useGroupClientState(state => ({
        type: state.type,
        id: state.id
    }), true)

    const updateGroupsByTypeCache = useUpdateGroupsByTypeCache()
    const updateUsersByGroupCache = useUpdateUsersByGroupCache()

    return useMutation({
        gcTime: 0,
        ...(options?.mutationOptions ?? null),
        mutationKey: [namespace, 'delete'],
        mutationFn: ({ id }) => remove({
            path: `/groups/${id ?? clientState?.id}`,
            returnsData: false
        }),
        onSuccess: ({ ok }, variables) => {
            if(!ok) {
                return
            }

            queryClient.removeQueries({
                queryKey: getGroupQuery(
                    variables.type ?? clientState?.type,
                    variables.id ?? clientState?.id
                ).queryKey,
                exact: true
            })

            // Update the list of groups-of-type to reflect the deleted group
            updateGroupsByTypeCache(variables, {
                method: 'invalidateQueries',
                recount: true
            })

            // Remove the cached list of group members to reflect the deleted group
            updateUsersByGroupCache(variables, 'removeQueries')
        }
    })
}

export const useGroupDeleted = () => {
    const [deleted = false] = useMutationState({
        filters: {
            mutationKey: [namespace, 'delete'],
            exact: true,
            status: 'success'
        },
        select: ({ state }) => !!state?.data?.ok
    })

    return deleted
}

export const useAddGroupMembers = () => {
    const updateGroupsByTypeCache = useUpdateGroupsByTypeCache()
    const updateUsersByGroupCache = useUpdateUsersByGroupCache()

    return useMutation({
        mutationFn: ({ id, members = [] }) => {
            const body = members.map(({ id: userId }) => ({ userId }))

            return post({
                path: `/groups/${id}/members`,
                body,
                returnsData: false
            })
        },
        onSuccess: async ({ ok }, variables) => {
            if(!ok) {
                return
            }

            // Update the list of groups-of-type to reflect the new member count
            updateGroupsByTypeCache(variables, { method: 'invalidateQueries' })

            // Update the list of group members to reflect the added members
            updateUsersByGroupCache(variables, 'invalidateQueries')
        }
    })
}

export const useRemoveGroupMember = () => {
    const updateGroupsByTypeCache = useUpdateGroupsByTypeCache()
    const updateUsersByGroupCache = useUpdateUsersByGroupCache()

    return useMutation({
        mutationFn: ({ id, userId }) => remove({
            path: `/groups/${id}/members/${userId}`,
            returnsData: false
        }),
        onSuccess: ({ ok }, variables) => {
            if(!ok) {
                return
            }

            // Update the list of groups-of-type to reflect the new member count
            updateGroupsByTypeCache(variables, { method: 'invalidateQueries' })

            // Update the list of group members to reflect the removed member
            updateUsersByGroupCache(variables, 'invalidateQueries')
        }
    })
}

export const useAddGroupGrant = (options = {}) => {
    const queryClient = useQueryClient()

    return useMutation({
        ...(options?.mutationOptions ?? null),
        mutationFn: ({ id, body }) => post({
            path: `/groups/${id}/grants`,
            body
        }),
        onSuccess: ({ ok, response: grants }, { type, id }) => ok && queryClient.setQueryData(
            getGroupQuery(type, id).queryKey,
            old => ({
                ...old,
                response: {
                    ...old.response,
                    grants
                }
            })
        )
    })
}

export const useRemoveGroupGrant = (options = {}) => {
    const queryClient = useQueryClient()

    return useMutation({
        ...(options?.mutationOptions ?? null),
        mutationFn: ({ id, body }) => remove({
            path: `/groups/${id}/grant`,
            body,
            returnsData: false
        }),
        onSuccess: ({ ok }, { type, id, body }) => ok && queryClient.setQueryData(
            getGroupQuery(type, id).queryKey,
            old => {
                const grant = old.response.grants.find(({ role }) => role.id === body.roleId)

                if(grant.users) {
                    grant.users = grant.users.filter(({ id }) => id !== body.userId)
                }

                return {
                    ...old,
                    response: {
                        ...old.response,
                        grants: old.response.grants.filter(({ users }) => !!users.length)
                    }
                }
            }
        )
    })
}

const useUpdateGroupsByTypeCache = () => {
    const queryClient = useQueryClient()

    return ({ type }, options = {}) => {
        if(!options.method || !queryClient[options.method]) {
            return
        }

        const groupsQueryKey = getGroupsInfiniteQuery({ types: [type] }).queryKey

        queryClient[options.method]({
            predicate: ({ queryKey }) => {
                if(options.recount && queryKey[0] === 'count' && queryKey[1] === groupsQueryKey[0]) {
                    return true
                }

                if(queryKey[0] !== groupsQueryKey[0]) {
                    return false
                }

                const {
                    types,
                    path
                } = queryKey[1] ?? {}

                return !types || types.includes(type) || (path === '/units' && types.includes('group'))
            }
        })
    }
}